import { BrowserRouter, Navigate, NavLink, Route, Routes } from 'react-router-dom';
import { useEffect } from 'react';
import { DebugHUD } from './components/DebugHUD';
import { useTelegramAuth } from './hooks/useTelegramAuth';
import TasksView from './views/TasksView';
import RemindersView from './views/RemindersView';
import SettingsView from './views/SettingsView';
import { SummaryHeader } from './components/SummaryHeader';
import { useQuery } from '@tanstack/react-query';
import { useMiniAppApi } from './lib/context';
import { AuthStateContext, useAuthState } from './lib/auth-context';
import { AuthStatusBanner } from './components/AuthStatusBanner';
import { AppErrorBoundary } from './components/AppErrorBoundary';

function AuthGate({ children }: { children: JSX.Element }) {
  const auth = useTelegramAuth();

  useEffect(() => {
    console.log('[AuthGate] status', {
      status: auth.status,
      source: auth.session?.source,
      userId: auth.session?.userId,
      telegramUserId: auth.session?.telegramUserId,
    });
  }, [auth.status, auth.session?.source, auth.session?.userId, auth.session?.telegramUserId]);

  const content =
    auth.status === 'loading' ? (
      <div className="screen">
        <h1>Авторизация…</h1>
        <p>Проверяем данные Telegram</p>
      </div>
    ) : (
      children
    );

  return (
    <AuthStateContext.Provider value={auth}>
      {content}
    </AuthStateContext.Provider>
  );
}

function Navigation() {
  return (
    <nav className="nav">
      <NavLink to="/tasks" className={({ isActive }) => (isActive ? 'nav__link nav__link--active' : 'nav__link')}>
        Задачи
      </NavLink>
      <NavLink to="/reminders" className={({ isActive }) => (isActive ? 'nav__link nav__link--active' : 'nav__link')}>
        Напоминания
      </NavLink>
      <NavLink to="/settings" className={({ isActive }) => (isActive ? 'nav__link nav__link--active' : 'nav__link')}>
        Настройки
      </NavLink>
    </nav>
  );
}

function AppContent() {
  const api = useMiniAppApi();
  const auth = useAuthState();
  const userIdentifier = auth.session?.userId ?? auth.session?.telegramUserId;
  const canQuery =
    !!userIdentifier &&
    (auth.status === 'authenticated' ||
      auth.status === 'cached-session' ||
      auth.status === 'read-only');

  useEffect(() => {
    console.log('[AppContent] auth snapshot', {
      status: auth.status,
      userId: auth.session?.userId,
      telegramUserId: auth.session?.telegramUserId,
      source: auth.session?.source,
    });
  }, [auth.status, auth.session?.source, auth.session?.userId, auth.session?.telegramUserId]);

  const tasksQuery = useQuery({
    queryKey: ['tasks', userIdentifier, 'count'],
    queryFn: () => api.getTasks(userIdentifier || undefined),
    enabled: canQuery,
    select: data => data.filter(t => t.status === 'pending').length,
  });

  const remindersQuery = useQuery({
    queryKey: ['reminders', userIdentifier, 'count'],
    queryFn: () => api.getReminders(userIdentifier || undefined),
    enabled: canQuery,
    select: data => data.filter(r => r.status === 'pending').length,
  });

  return (
    <div className="layout">
      <AuthStatusBanner />
      <SummaryHeader 
        tasksCount={tasksQuery.data} 
        remindersCount={remindersQuery.data} 
      />
      <Navigation />
      <main className="content">
        <Routes>
          <Route path="/tasks" element={<TasksView />} />
          <Route path="/reminders" element={<RemindersView />} />
          <Route path="/settings" element={<SettingsView />} />
          <Route path="*" element={<Navigate to="/tasks" replace />} />
        </Routes>
      </main>
      <DebugHUD enabled={import.meta.env.DEV ?? import.meta.env.MODE === 'development'} />
    </div>
  );
}

export default function App() {
  return (
    <AppErrorBoundary>
      <BrowserRouter>
        <AuthGate>
          <AppContent />
        </AuthGate>
      </BrowserRouter>
    </AppErrorBoundary>
  );
}
